import React, { useState, useEffect } from "react";
import { T } from "../lib/theme";
import { Spin } from "./Atoms";
import { US_STATES } from "../lib/usStates";

function timeAgo(iso){
  if(!iso)return "";
  const min=Math.floor((Date.now()-new Date(iso).getTime())/60000);
  if(min<1)return "just now";
  if(min<60)return `${min}m ago`;
  const hr=Math.floor(min/60);
  if(hr<24)return `${hr}h ago`;
  return `${Math.floor(hr/24)}d ago`;
}

// The six household_limits fields, in display order. Four carry a `used`
// figure from the server (real consumption caps); refreshMinAgeDays and
// refreshMileageThreshold don't — they're throttle parameters, not
// consumption limits, so showing a fabricated "usage" for them would be
// misleading. unit is cosmetic only, appended to the displayed number.
const LIMIT_FIELDS=[
  {key:"dailyEnrichCap",label:"Daily VinAudit + Clyde research",unit:""},
  {key:"lifetimePlateLookupCap",label:"Lifetime plate lookups",unit:""},
  {key:"dailyClydeChatInputTokenCap",label:"Daily Clyde chat input tokens",unit:""},
  {key:"dailyDealerSearchCap",label:"Daily dealer searches",unit:""},
  {key:"dailyVinPhotoCap",label:"Daily VIN-from-photo lookups",unit:""},
  {key:"refreshMinAgeDays",label:"Refresh minimum age",unit:" days"},
  {key:"refreshMileageThreshold",label:"Refresh mileage threshold",unit:" mi"},
  {key:"conversationRetentionDays",label:"Conversation retention",unit:" days"},
];

// Reachable only when HomeFleetApp's own accessLevel check (from /api/me)
// renders the header's Admin link at all — but that's UX, not security.
// The real gate is server-side: every /api/admin/* endpoint independently
// checks access_level==='admin' via requireAdmin() and returns 403
// otherwise, regardless of what this component does or doesn't render.
export function AdminDashboard(){
  const[summary,setSummary]=useState(null);
  const[summaryError,setSummaryError]=useState("");
  const[users,setUsers]=useState(null);
  const[usersError,setUsersError]=useState("");
  const[activity,setActivity]=useState(null);
  const[activityError,setActivityError]=useState("");
  const[busyUserId,setBusyUserId]=useState(null);
  const[actionError,setActionError]=useState("");
  const[resettingUserId,setResettingUserId]=useState(null);
  const[resetError,setResetError]=useState("");
  const[resetResult,setResetResult]=useState(null); // {email, link, expiresAt}
  const[copied,setCopied]=useState(false);
  const[pendingRequests,setPendingRequests]=useState(null);
  const[pendingRequestsError,setPendingRequestsError]=useState("");
  const[fullAccessRequests,setFullAccessRequests]=useState(null);
  const[fullAccessRequestsError,setFullAccessRequestsError]=useState("");
  const[unreviewedSignups,setUnreviewedSignups]=useState(null);
  const[unreviewedSignupsError,setUnreviewedSignupsError]=useState("");
  const[editingUserId,setEditingUserId]=useState(null);
  const[editName,setEditName]=useState("");
  const[editPhone,setEditPhone]=useState("");
  const[editState,setEditState]=useState("");
  const[editZip,setEditZip]=useState("");
  const[savingUserId,setSavingUserId]=useState(null);
  const[editError,setEditError]=useState("");
  const[generatedTokens,setGeneratedTokens]=useState({}); // {[userId]: {link}} — local only, never persisted or refetched; gone on reload by design
  const[tokenActionUserId,setTokenActionUserId]=useState(null);
  const[tokenActionError,setTokenActionError]=useState("");
  const[expandedLimitsUserId,setExpandedLimitsUserId]=useState(null);
  const[householdLimits,setHouseholdLimits]=useState(null);
  const[limitsLoading,setLimitsLoading]=useState(false);
  const[limitsError,setLimitsError]=useState("");
  const[limitEdits,setLimitEdits]=useState({}); // {[fieldKey]: input string} — local edit buffer for the expanded panel
  const[savingLimitField,setSavingLimitField]=useState(null);
  const[activityCollapsed,setActivityCollapsed]=useState(false);

  const loadSummary=async()=>{
    setSummaryError("");
    try{
      const res=await fetch("/api/admin/summary");
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not load fleet overview.");
      setSummary(body);
    }catch(e){setSummaryError(e.message||"Could not load fleet overview.");}
  };
  const loadUsers=async()=>{
    setUsersError("");
    try{
      const res=await fetch("/api/admin/users");
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not load users.");
      setUsers(body.users||[]);
    }catch(e){setUsersError(e.message||"Could not load users.");}
  };
  const loadActivity=async()=>{
    setActivityError("");
    try{
      const res=await fetch("/api/admin/activity");
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not load activity.");
      setActivity(body.activity||[]);
    }catch(e){setActivityError(e.message||"Could not load activity.");}
  };
  // Deliberately its own load, not derived from loadActivity()'s 50-row
  // feed — that cap would silently hide an older, still-unfulfilled
  // request once 50 other activity rows have happened since. Backed by
  // GET /api/admin/reset-requests, which computes "still pending"
  // server-side with no row limit.
  const loadResetRequests=async()=>{
    setPendingRequestsError("");
    try{
      const res=await fetch("/api/admin/reset-requests");
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not load pending reset requests.");
      setPendingRequests(body.requests||[]);
    }catch(e){setPendingRequestsError(e.message||"Could not load pending reset requests.");}
  };
  // Deliberately its own load, same reasoning as loadResetRequests below
  // it — not derived from loadActivity()'s 50-row feed, which could
  // silently hide an older still-outstanding request once 50 other
  // activity rows have happened since. Backed by GET /api/admin/
  // full-access-requests, which resolves "still outstanding" directly
  // off each user's current access_level, with no row limit.
  const loadFullAccessRequests=async()=>{
    setFullAccessRequestsError("");
    try{
      const res=await fetch("/api/admin/full-access-requests");
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not load full access requests.");
      setFullAccessRequests(body.requests||[]);
    }catch(e){setFullAccessRequestsError(e.message||"Could not load full access requests.");}
  };
  // Deliberately its own load, same reasoning as the two above — not
  // derived from loadActivity()'s 50-row feed. Backed by GET /api/admin/
  // unreviewed-signups, which resolves "not yet reviewed" directly off
  // each user's activity_log history, with no row limit.
  const loadUnreviewedSignups=async()=>{
    setUnreviewedSignupsError("");
    try{
      const res=await fetch("/api/admin/unreviewed-signups");
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not load unreviewed signups.");
      setUnreviewedSignups(body.signups||[]);
    }catch(e){setUnreviewedSignupsError(e.message||"Could not load unreviewed signups.");}
  };
  useEffect(()=>{loadSummary();loadUsers();loadActivity();loadResetRequests();loadFullAccessRequests();loadUnreviewedSignups();},[]);

  const approve=async(userId,accessLevel)=>{
    setBusyUserId(userId);setActionError("");
    try{
      const res=await fetch("/api/admin/approve",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({userId,accessLevel})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not update access.");
      await loadUsers();
      // A promotion out of 'demo' is exactly the condition
      // full-access-requests.js and unreviewed-signups.js both resolve
      // off of — refresh both so a request/signup disappears from its
      // panel without a reload (this also covers the Deactivate button
      // being used as the unreviewed-signups panel's reject path).
      await loadFullAccessRequests();
      await loadUnreviewedSignups();
      await loadSummary();
    }catch(e){setActionError(e.message||"Could not update access.");}
    setBusyUserId(null);
  };

  // review-signup.js now promotes access_level to 'live' in the same
  // call, not just an audit marker — so this refreshes the same set of
  // panels approve() does, not just unreviewedSignups.
  const markReviewed=async(userId)=>{
    setBusyUserId(userId);setActionError("");
    try{
      const res=await fetch("/api/admin/review-signup",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({userId})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not mark reviewed.");
      await loadUnreviewedSignups();
      await loadUsers();
      await loadFullAccessRequests();
      await loadSummary();
    }catch(e){setActionError(e.message||"Could not mark reviewed.");}
    setBusyUserId(null);
  };

  // Only the token comes back from the server (functions/api/admin/
  // reset-token.js) — the full link is built here from window.location.origin
  // so the backend never has to assume or hardcode a deployed domain.
  const resetPassword=async(userId,userEmail)=>{
    setResettingUserId(userId);setResetError("");setCopied(false);
    try{
      const res=await fetch("/api/admin/reset-token",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({userId})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not generate a reset link.");
      setResetResult({email:userEmail,link:`${window.location.origin}/?reset=${body.token}`,expiresAt:body.expiresAt});
    }catch(e){setResetError(e.message||"Could not generate a reset link.");}
    setResettingUserId(null);
  };

  const copyLink=()=>{
    if(!resetResult)return;
    navigator.clipboard.writeText(resetResult.link).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2000);});
  };

  // Generate and Regenerate are the same underlying call — reset-token.js
  // already invalidates any prior unused token before issuing a new one,
  // so "regenerate" from a Sent row needs no separate endpoint or logic.
  // Refetching afterward is what actually moves the row: a fresh token's
  // sent_at is NULL until mark-sent runs again, so the row naturally
  // falls back into the Pending bucket on the next load.
  const generateToken=async(userId)=>{
    setTokenActionUserId(userId);setTokenActionError("");
    try{
      const res=await fetch("/api/admin/reset-token",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({userId})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not generate a reset link.");
      setGeneratedTokens(prev=>({...prev,[userId]:{link:`${window.location.origin}/?reset=${body.token}`}}));
      await loadResetRequests();
    }catch(e){setTokenActionError(e.message||"Could not generate a reset link.");}
    setTokenActionUserId(null);
  };

  // One click does both: copies the link this browser is still holding
  // in memory, and tells the server it was sent. There's no standalone
  // "just copy" step — once marked sent, the row moves to Sent and does
  // not offer Copy Link again, since the raw token was never stored and
  // can't honestly be re-offered after a reload.
  const copyAndMarkSent=async(userId)=>{
    const held=generatedTokens[userId];
    if(!held)return;
    setTokenActionUserId(userId);setTokenActionError("");
    try{
      await navigator.clipboard.writeText(held.link);
      const res=await fetch("/api/admin/mark-sent",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({userId})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not mark as sent.");
      await loadResetRequests();
    }catch(e){setTokenActionError(e.message||"Could not mark as sent.");}
    setTokenActionUserId(null);
  };

  const loadHouseholdLimits=async(householdId)=>{
    setLimitsLoading(true);setLimitsError("");
    try{
      const res=await fetch(`/api/admin/household-limits?householdId=${encodeURIComponent(householdId)}`);
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not load limits.");
      setHouseholdLimits(body.limits);
      // Filled with the override value when one exists; empty otherwise,
      // so the input's placeholder (the resolved value, which equals the
      // default when there's no override) shows through.
      const edits={};
      for(const f of LIMIT_FIELDS){
        const d=body.limits[f.key];
        edits[f.key]=d&&d.isOverride?String(d.value):"";
      }
      setLimitEdits(edits);
    }catch(e){setLimitsError(e.message||"Could not load limits.");}
    setLimitsLoading(false);
  };

  const toggleLimits=(u)=>{
    if(expandedLimitsUserId===u.id){
      setExpandedLimitsUserId(null);setHouseholdLimits(null);setLimitsError("");
    }else{
      setExpandedLimitsUserId(u.id);setHouseholdLimits(null);
      loadHouseholdLimits(u.householdId);
    }
  };

  const saveLimitField=async(u,fieldKey)=>{
    setSavingLimitField(fieldKey);setLimitsError("");
    try{
      const res=await fetch("/api/admin/update-limit",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({householdId:u.householdId,field:fieldKey,value:limitEdits[fieldKey]})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not save.");
      await loadHouseholdLimits(u.householdId);
    }catch(e){setLimitsError(e.message||"Could not save.");}
    setSavingLimitField(null);
  };

  const startEdit=(u)=>{
    setEditingUserId(u.id);
    setEditName(u.fullName||"");
    setEditPhone(u.phone||"");
    setEditState(u.state||"");
    setEditZip(u.zip||"");
    setEditError("");
  };
  const cancelEdit=()=>{
    setEditingUserId(null);setEditError("");
  };
  const saveEdit=async(userId)=>{
    setSavingUserId(userId);setEditError("");
    try{
      const res=await fetch("/api/admin/update-user",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({userId,full_name:editName,phone:editPhone,state:editState,zip:editZip})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not save.");
      setEditingUserId(null);
      await loadUsers();
    }catch(e){setEditError(e.message||"Could not save.");}
    setSavingUserId(null);
  };

  const th={padding:"8px 10px",color:T.textSm,fontWeight:600,fontSize:11,textTransform:"uppercase",letterSpacing:".04em"};
  const td={padding:"8px 10px",fontSize:13};

  return(
    <div style={{maxWidth:980,margin:"0 auto",padding:"20px 16px 52px"}}>
      <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:16}}>Admin Dashboard</div>

      <div style={{background:T.card,borderRadius:16,border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"18px 20px",marginBottom:20}}>
        <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:12}}>Fleet Overview</div>
        {summaryError&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{summaryError}</div>}
        {!summary&&!summaryError&&<div style={{display:"flex",alignItems:"center",gap:8,color:T.textMd,fontSize:13}}><Spin sz={16}/>Loading...</div>}
        {summary&&(
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <div style={{flex:"1 1 140px",background:T.slateL,borderRadius:10,padding:"12px 14px"}}>
              <div style={{fontSize:22,fontWeight:800,color:T.text}}>{summary.userCount.toLocaleString()}</div>
              <div style={{fontSize:11,color:T.textSm,fontWeight:600,textTransform:"uppercase",letterSpacing:".04em"}}>Users</div>
            </div>
            <div style={{flex:"1 1 140px",background:T.slateL,borderRadius:10,padding:"12px 14px"}}>
              <div style={{fontSize:22,fontWeight:800,color:T.text}}>{summary.vehicleCount.toLocaleString()}</div>
              <div style={{fontSize:11,color:T.textSm,fontWeight:600,textTransform:"uppercase",letterSpacing:".04em"}}>Vehicles</div>
            </div>
            <div style={{flex:"1 1 140px",background:T.slateL,borderRadius:10,padding:"12px 14px"}}>
              <div style={{fontSize:22,fontWeight:800,color:T.text}}>${summary.totalValue.toLocaleString()}</div>
              <div style={{fontSize:11,color:T.textSm,fontWeight:600,textTransform:"uppercase",letterSpacing:".04em"}}>Total Value</div>
            </div>
          </div>
        )}
        {summary&&summary.estimatedCount>0&&(
          <div style={{fontSize:11,color:T.textSm,marginTop:10}}>{summary.pricedCount.toLocaleString()} priced from real/researched data, {summary.estimatedCount.toLocaleString()} estimated by formula — included in the total either way.</div>
        )}
      </div>

      <div style={{background:T.card,borderRadius:16,border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"18px 20px",marginBottom:20}}>
        <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:12}}>Pending Password Reset Requests</div>
        {pendingRequestsError&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{pendingRequestsError}</div>}
        {tokenActionError&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{tokenActionError}</div>}
        {!pendingRequests&&!pendingRequestsError&&<div style={{display:"flex",alignItems:"center",gap:8,color:T.textMd,fontSize:13}}><Spin sz={16}/>Loading...</div>}
        {pendingRequests&&pendingRequests.length===0&&<div style={{fontSize:13,color:T.textSm}}>No pending reset requests.</div>}
        {pendingRequests&&pendingRequests.length>0&&(()=>{
          // Bucket order (Pending, Sent, Reset) and the oldest-first order
          // within each bucket both come from the array's existing order —
          // GET /api/admin/reset-requests already returns oldest-first, so
          // filtering preserves that; nothing here re-sorts.
          const pendingRows=pendingRequests.filter(r=>!r.completedAt&&!r.sentAt);
          const sentRows=pendingRequests.filter(r=>!r.completedAt&&r.sentAt);
          const resetRows=pendingRequests.filter(r=>r.completedAt);
          const rowLabel=(r)=><>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>{r.fullName||r.email}</div>
            {r.fullName&&<div style={{fontSize:11,color:T.textSm}}>{r.email}</div>}
          </>;
          return(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {pendingRows.map(r=>(
                <div key={r.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"8px 10px",background:T.amberL,borderRadius:8}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                      <span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:4,background:T.amber,color:"#fff",textTransform:"uppercase"}}>Pending</span>
                    </div>
                    {rowLabel(r)}
                    <div style={{fontSize:11,color:T.textSm}}>Requested {timeAgo(r.requestedAt)}</div>
                  </div>
                  {generatedTokens[r.userId]?(
                    <button onClick={()=>copyAndMarkSent(r.userId)} disabled={tokenActionUserId===r.userId} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.teal,border:"none",borderRadius:6,padding:"6px 12px",cursor:tokenActionUserId===r.userId?"default":"pointer",opacity:tokenActionUserId===r.userId?.6:1,whiteSpace:"nowrap"}}>{tokenActionUserId===r.userId?"Copying...":"Copy Link"}</button>
                  ):(
                    <button onClick={()=>generateToken(r.userId)} disabled={tokenActionUserId===r.userId} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.teal,border:"none",borderRadius:6,padding:"6px 12px",cursor:tokenActionUserId===r.userId?"default":"pointer",opacity:tokenActionUserId===r.userId?.6:1,whiteSpace:"nowrap"}}>{tokenActionUserId===r.userId?"Generating...":"Generate Link"}</button>
                  )}
                </div>
              ))}
              {sentRows.map(r=>(
                <div key={r.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"8px 10px",background:T.slateL,borderRadius:8}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                      <span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:4,background:T.teal,color:"#fff",textTransform:"uppercase"}}>Sent</span>
                    </div>
                    {rowLabel(r)}
                    <div style={{fontSize:11,color:T.textSm}}>Sent {timeAgo(r.sentAt)} · requested {timeAgo(r.requestedAt)}</div>
                  </div>
                  <button onClick={()=>generateToken(r.userId)} disabled={tokenActionUserId===r.userId} title="Generates a new link and invalidates the one already sent" style={{fontSize:11,fontWeight:700,color:T.textMd,background:T.card,border:`1px solid ${T.border}`,borderRadius:6,padding:"6px 12px",cursor:tokenActionUserId===r.userId?"default":"pointer",opacity:tokenActionUserId===r.userId?.6:1,whiteSpace:"nowrap"}}>{tokenActionUserId===r.userId?"Generating...":"Regenerate (invalidates sent link)"}</button>
                </div>
              ))}
              {resetRows.length>0&&(
                <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:(pendingRows.length||sentRows.length)?8:0,paddingTop:(pendingRows.length||sentRows.length)?8:0,borderTop:(pendingRows.length||sentRows.length)?`1px solid ${T.border}`:"none",opacity:.6}}>
                  {resetRows.map(r=>(
                    <div key={r.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 10px"}}>
                      <span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:4,background:T.slateL,color:T.textMd,textTransform:"uppercase"}}>Reset</span>
                      <span style={{fontSize:12,fontWeight:600,color:T.textMd}}>{r.fullName||r.email}</span>
                      <span style={{fontSize:11,color:T.textSm}}>Completed {timeAgo(r.completedAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      <div style={{background:T.card,borderRadius:16,border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"18px 20px",marginBottom:20}}>
        <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:12}}>Full Access Requests</div>
        {fullAccessRequestsError&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{fullAccessRequestsError}</div>}
        {!fullAccessRequests&&!fullAccessRequestsError&&<div style={{display:"flex",alignItems:"center",gap:8,color:T.textMd,fontSize:13}}><Spin sz={16}/>Loading...</div>}
        {fullAccessRequests&&fullAccessRequests.length===0&&<div style={{fontSize:13,color:T.textSm}}>No outstanding requests.</div>}
        {fullAccessRequests&&fullAccessRequests.length>0&&(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {fullAccessRequests.map(r=>(
              <div key={r.userId} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"8px 10px",background:T.tealL,borderRadius:8}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{r.fullName||r.email}</div>
                  {r.fullName&&<div style={{fontSize:11,color:T.textSm}}>{r.email}</div>}
                  <div style={{fontSize:11,color:T.textSm}}>Requested {timeAgo(r.requestedAt)}</div>
                </div>
                <button onClick={()=>approve(r.userId,"live")} disabled={busyUserId===r.userId} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.teal,border:"none",borderRadius:6,padding:"6px 12px",cursor:busyUserId===r.userId?"default":"pointer",opacity:busyUserId===r.userId?.6:1,whiteSpace:"nowrap"}}>{busyUserId===r.userId?"Approving...":"Approve Live"}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{background:T.card,borderRadius:16,border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"18px 20px",marginBottom:20}}>
        <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:12}}>Unreviewed Signups</div>
        {unreviewedSignupsError&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{unreviewedSignupsError}</div>}
        {!unreviewedSignups&&!unreviewedSignupsError&&<div style={{display:"flex",alignItems:"center",gap:8,color:T.textMd,fontSize:13}}><Spin sz={16}/>Loading...</div>}
        {unreviewedSignups&&unreviewedSignups.length===0&&<div style={{fontSize:13,color:T.textSm}}>No unreviewed signups.</div>}
        {unreviewedSignups&&unreviewedSignups.length>0&&(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {unreviewedSignups.map(s=>(
              <div key={s.userId} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"8px 10px",background:T.slateL,borderRadius:8}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{s.fullName||s.email}</div>
                  {s.fullName&&<div style={{fontSize:11,color:T.textSm}}>{s.email}</div>}
                  <div style={{fontSize:11,color:T.textSm}}>Signed up {timeAgo(s.createdAt)}</div>
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button onClick={()=>approve(s.userId,"deactivated")} disabled={busyUserId===s.userId} style={{fontSize:11,fontWeight:700,color:T.red,background:T.card,border:`1px solid ${T.red}`,borderRadius:6,padding:"6px 12px",cursor:busyUserId===s.userId?"default":"pointer",opacity:busyUserId===s.userId?.6:1,whiteSpace:"nowrap"}}>Deactivate</button>
                  <button onClick={()=>markReviewed(s.userId)} disabled={busyUserId===s.userId} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.teal,border:"none",borderRadius:6,padding:"6px 12px",cursor:busyUserId===s.userId?"default":"pointer",opacity:busyUserId===s.userId?.6:1,whiteSpace:"nowrap"}}>{busyUserId===s.userId?"Saving...":"Mark Reviewed"}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {resetError&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{resetError}</div>}
      {resetResult&&(
        <div style={{background:T.slateL,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px",marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:6}}>Reset link for {resetResult.email}</div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input readOnly value={resetResult.link} onFocus={e=>e.target.select()} style={{flex:1,fontSize:12,padding:"7px 9px",border:`1px solid ${T.border}`,borderRadius:6,fontFamily:"monospace",background:T.card}}/>
            <button onClick={copyLink} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.teal,border:"none",borderRadius:6,padding:"7px 12px",cursor:"pointer"}}>{copied?"Copied!":"Copy"}</button>
          </div>
          <div style={{fontSize:11,color:T.textSm,marginTop:6}}>Expires in 24 hours. Send this link to the user yourself — nothing is emailed automatically.</div>
        </div>
      )}

      <div style={{background:T.card,borderRadius:16,border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"18px 20px",marginBottom:20}}>
        <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:12}}>Users</div>
        {usersError&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{usersError}</div>}
        {actionError&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{actionError}</div>}
        {!users&&!usersError&&<div style={{display:"flex",alignItems:"center",gap:8,color:T.textMd,fontSize:13}}><Spin sz={16}/>Loading users...</div>}
        {users&&(
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{textAlign:"left",borderBottom:`1px solid ${T.border}`}}>
                  <th style={th}>User</th>
                  <th style={th}>Access</th>
                  <th style={th}>Signed up</th>
                  <th style={th}>Last login</th>
                  <th style={th}>Vehicles</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u=>(
                  <React.Fragment key={u.id}>
                  <tr style={{borderBottom:`1px solid ${T.border}`}}>
                    <td style={td}>
                      {editingUserId===u.id?(
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          <input value={editName} onChange={e=>setEditName(e.target.value)} placeholder="Full name" style={{fontSize:12,padding:"5px 7px",border:`1px solid ${T.border}`,borderRadius:5,width:160,boxSizing:"border-box"}}/>
                          <input value={editPhone} onChange={e=>setEditPhone(e.target.value)} placeholder="Phone (optional)" style={{fontSize:12,padding:"5px 7px",border:`1px solid ${T.border}`,borderRadius:5,width:160,boxSizing:"border-box"}}/>
                          <select value={editState} onChange={e=>setEditState(e.target.value)} style={{fontSize:12,padding:"5px 7px",border:`1px solid ${T.border}`,borderRadius:5,width:160,boxSizing:"border-box",background:T.card}}><option value="">State (optional)</option>{US_STATES.map(s=><option key={s.code} value={s.code}>{s.name}</option>)}</select>
                          <input value={editZip} onChange={e=>setEditZip(e.target.value.replace(/[^0-9]/g,"").slice(0,5))} placeholder="ZIP (optional)" style={{fontSize:12,padding:"5px 7px",border:`1px solid ${T.border}`,borderRadius:5,width:160,boxSizing:"border-box"}}/>
                          {editError&&<div style={{fontSize:11,color:T.red}}>{editError}</div>}
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>saveEdit(u.id)} disabled={savingUserId===u.id} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.teal,border:"none",borderRadius:5,padding:"4px 9px",cursor:savingUserId===u.id?"default":"pointer",opacity:savingUserId===u.id?.6:1}}>{savingUserId===u.id?"Saving...":"Save"}</button>
                            <button onClick={cancelEdit} disabled={savingUserId===u.id} style={{fontSize:11,fontWeight:700,color:T.textMd,background:T.card,border:`1px solid ${T.border}`,borderRadius:5,padding:"4px 9px",cursor:savingUserId===u.id?"default":"pointer"}}>Cancel</button>
                          </div>
                        </div>
                      ):(
                        <div>
                          <div style={{color:T.text,fontWeight:600}}>{u.fullName?u.fullName:<span style={{color:T.textSm,fontStyle:"italic",fontWeight:400}}>No name on file</span>}</div>
                          <div style={{color:T.textSm,fontSize:12}}>{u.email}</div>
                          {u.phone&&<div style={{color:T.textSm,fontSize:12}}>{u.phone}</div>}
                          {u.state&&<div style={{color:T.textSm,fontSize:12}}>{u.state}</div>}
                          <button type="button" onClick={()=>startEdit(u)} style={{fontSize:11,color:T.teal,fontWeight:600,background:"none",border:"none",padding:0,marginTop:3,cursor:"pointer"}}>{u.fullName?"Edit":"Add name"}</button>
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:4,background:u.accessLevel==="pending"?T.amberL:u.accessLevel==="admin"?T.tealL:u.accessLevel==="deactivated"?T.redL:T.slateL,color:u.accessLevel==="pending"?T.amber:u.accessLevel==="admin"?T.teal:u.accessLevel==="deactivated"?T.red:T.textMd}}>{u.accessLevel}</span>
                    </td>
                    <td style={{...td,color:T.textMd}}>{u.createdAt?u.createdAt.slice(0,10):"—"}</td>
                    <td style={{...td,color:T.textMd}}>{u.lastLoginAt?u.lastLoginAt.slice(0,10):"Never"}</td>
                    <td style={{...td,color:T.textMd}}>{u.vehicleCount}{u.exampleVehicleCount>0?` (+${u.exampleVehicleCount} example)`:""}</td>
                    <td style={td}>
                      {u.accessLevel==="pending"&&(
                        <div style={{display:"flex",gap:6}}>
                          <button onClick={()=>approve(u.id,"live")} disabled={busyUserId===u.id} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.teal,border:"none",borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Approve Live</button>
                          <button onClick={()=>approve(u.id,"demo")} disabled={busyUserId===u.id} style={{fontSize:11,fontWeight:700,color:T.textMd,background:T.slateL,border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Approve Demo</button>
                        </div>
                      )}
                      {(u.accessLevel==="demo"||u.accessLevel==="live")&&(
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          <button onClick={()=>approve(u.id,"live")} disabled={busyUserId===u.id||u.accessLevel==="live"} style={{fontSize:11,fontWeight:700,color:u.accessLevel==="live"?T.teal:"#fff",background:u.accessLevel==="live"?T.tealL:T.teal,border:"none",borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id||u.accessLevel==="live"?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Live</button>
                          <button onClick={()=>approve(u.id,"demo")} disabled={busyUserId===u.id||u.accessLevel==="demo"} style={{fontSize:11,fontWeight:700,color:T.textMd,background:u.accessLevel==="demo"?T.slateL:"#fff",border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id||u.accessLevel==="demo"?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Demo</button>
                          <button onClick={()=>approve(u.id,"deactivated")} disabled={busyUserId===u.id} style={{fontSize:11,fontWeight:700,color:T.red,background:T.card,border:`1px solid ${T.red}`,borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Deactivate</button>
                          <button onClick={()=>approve(u.id,"admin")} disabled={busyUserId===u.id} style={{fontSize:11,fontWeight:700,color:T.teal,background:T.card,border:`1px solid ${T.teal}`,borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Promote to Admin</button>
                        </div>
                      )}
                      {u.accessLevel==="admin"&&!u.isProtected&&(
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:11,color:T.teal,fontWeight:600}}>Admin</span>
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>approve(u.id,"live")} disabled={busyUserId===u.id} style={{fontSize:11,fontWeight:700,color:T.textMd,background:T.card,border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Demote to Live</button>
                            <button onClick={()=>approve(u.id,"demo")} disabled={busyUserId===u.id} style={{fontSize:11,fontWeight:700,color:T.textMd,background:T.card,border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Demote to Demo</button>
                          </div>
                        </div>
                      )}
                      {u.accessLevel==="admin"&&u.isProtected&&(
                        <span style={{fontSize:11,color:T.teal,fontWeight:600}}>Admin (protected)</span>
                      )}
                      {u.accessLevel==="deactivated"&&(
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:11,color:T.red,fontWeight:600}}>Deactivated</span>
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>approve(u.id,"live")} disabled={busyUserId===u.id} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.teal,border:"none",borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Reactivate Live</button>
                            <button onClick={()=>approve(u.id,"demo")} disabled={busyUserId===u.id} style={{fontSize:11,fontWeight:700,color:T.textMd,background:T.slateL,border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 10px",cursor:busyUserId===u.id?"default":"pointer",opacity:busyUserId===u.id?.6:1}}>Reactivate Demo</button>
                          </div>
                        </div>
                      )}
                      <div style={{marginTop:6,display:"flex",gap:6}}>
                        <button onClick={()=>resetPassword(u.id,u.email)} disabled={resettingUserId===u.id} style={{fontSize:11,fontWeight:700,color:T.textMd,background:T.card,border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 10px",cursor:resettingUserId===u.id?"default":"pointer",opacity:resettingUserId===u.id?.6:1}}>{resettingUserId===u.id?"Generating...":"Reset Password"}</button>
                        <button onClick={()=>toggleLimits(u)} style={{fontSize:11,fontWeight:700,color:T.textMd,background:T.card,border:`1px solid ${T.border}`,borderRadius:6,padding:"5px 10px",cursor:"pointer"}}>{expandedLimitsUserId===u.id?"Hide Usage":"Usage & Limits"}</button>
                      </div>
                    </td>
                  </tr>
                  {expandedLimitsUserId===u.id&&(
                  <tr style={{borderBottom:`1px solid ${T.border}`}}>
                    <td colSpan={6} style={{padding:"12px 16px",background:T.slateL}}>
                      {limitsLoading&&<div style={{display:"flex",alignItems:"center",gap:8,color:T.textMd,fontSize:13}}><Spin sz={16}/>Loading limits...</div>}
                      {limitsError&&<div style={{fontSize:12,color:T.red,marginBottom:8}}>{limitsError}</div>}
                      {householdLimits&&(
                        <div>
                          {LIMIT_FIELDS.map(f=>{
                            const d=householdLimits[f.key];
                            if(!d)return null;
                            // d.value is genuinely null for
                            // conversationRetentionDays with no override
                            // set (no code-level default exists for it,
                            // unlike every other field here) — handled
                            // explicitly rather than assuming .toLocaleString()
                            // always has a real number to call it on.
                            const usageText=d.used!=null
                              ?`${d.used.toLocaleString()} / ${d.value.toLocaleString()}${f.unit} used (${d.window==="lifetime"?"lifetime":"last 24h"})`
                              :d.value!=null
                              ?`${d.value.toLocaleString()}${f.unit}${d.isOverride?"":" (default)"}`
                              :"No limit set";
                            return(
                              <div key={f.key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"7px 0",borderBottom:`1px solid ${T.border}`}}>
                                <div>
                                  <div style={{fontSize:12,fontWeight:600,color:T.text}}>{f.label}</div>
                                  <div style={{fontSize:11,color:T.textSm}}>{usageText}</div>
                                </div>
                                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                                  <input type="number" min="0" value={limitEdits[f.key]??""} onChange={e=>setLimitEdits(prev=>({...prev,[f.key]:e.target.value}))} placeholder={d.value!=null?String(d.value):"none"} style={{width:90,fontSize:12,padding:"5px 7px",border:`1px solid ${T.border}`,borderRadius:5,boxSizing:"border-box"}}/>
                                  <button onClick={()=>saveLimitField(u,f.key)} disabled={savingLimitField===f.key} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.teal,border:"none",borderRadius:5,padding:"5px 10px",cursor:savingLimitField===f.key?"default":"pointer",opacity:savingLimitField===f.key?.6:1}}>{savingLimitField===f.key?"...":"Save"}</button>
                                </div>
                              </div>
                            );
                          })}
                          <div style={{fontSize:11,color:T.textSm,marginTop:8}}>Clear a field and Save to remove an override and go back to the default.</div>
                        </div>
                      )}
                    </td>
                  </tr>
                  )}
                  </React.Fragment>
                ))}
                {users.length===0&&<tr><td style={{...td,color:T.textSm}} colSpan={6}>No users yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{background:T.card,borderRadius:16,border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"18px 20px"}}>
        <div onClick={()=>setActivityCollapsed(c=>!c)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",marginBottom:activityCollapsed?0:12}}>
          <div style={{fontSize:14,fontWeight:700,color:T.text}}>Recent Activity</div>
          <span style={{fontSize:11,fontWeight:700,color:T.textSm}}>{activityCollapsed?"Show ▾":"Hide ▴"}</span>
        </div>
        {!activityCollapsed&&(<>
        {activityError&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{activityError}</div>}
        {!activity&&!activityError&&<div style={{display:"flex",alignItems:"center",gap:8,color:T.textMd,fontSize:13}}><Spin sz={16}/>Loading activity...</div>}
        {activity&&activity.length===0&&<div style={{fontSize:13,color:T.textSm}}>No activity yet.</div>}
        {activity&&activity.length>0&&(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {activity.map(a=>(
              <div key={a.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,fontSize:12,padding:"7px 0",borderBottom:`1px solid ${T.border}`}}>
                <span style={{color:T.text,fontWeight:600}}>{a.email}</span>
                <span style={{color:T.textMd,flex:1,textAlign:"center"}}>{a.actionType}</span>
                <span style={{color:T.textSm}}>{a.timestamp?a.timestamp.slice(0,19).replace("T"," "):""}</span>
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}
